import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import type { DichotomousBulkPlan } from '../../dichotomous/dichotomousPlan';

type CompletionVariant = 'summary' | 'all-ok' | 'all-worn' | 'all-unreadable';

interface VariantCopy {
  title: string;
  /**
   * Degenerate framing shown instead of the overwrite warning: the annotator needs to know *why*
   * no breakpoint exists before agreeing to label a whole series with a single state.
   */
  body: string | null;
  /** Only the converged run warns about overwriting — the degenerate ones explain themselves. */
  overwriteWarning: string | null;
  confirm: string;
  secondary: string;
}

const VARIANT_COPY: Record<CompletionVariant, VariantCopy> = {
  summary: {
    title: 'labeling.dichotomous.summary.title',
    body: null,
    overwriteWarning: 'labeling.dichotomous.summary.overwriteWarning',
    confirm: 'labeling.dichotomous.summary.confirm',
    secondary: 'labeling.dichotomous.summary.cancel',
  },
  'all-worn': {
    title: 'labeling.dichotomous.degenerate.allWornTitle',
    body: 'labeling.dichotomous.degenerate.allWornBody',
    overwriteWarning: null,
    confirm: 'labeling.dichotomous.degenerate.applyAll',
    secondary: 'labeling.dichotomous.degenerate.fallbackManual',
  },
  'all-ok': {
    title: 'labeling.dichotomous.degenerate.allOkTitle',
    body: 'labeling.dichotomous.degenerate.allOkBody',
    overwriteWarning: null,
    confirm: 'labeling.dichotomous.degenerate.applyAll',
    secondary: 'labeling.dichotomous.degenerate.fallbackManual',
  },
  'all-unreadable': {
    title: 'labeling.dichotomous.degenerate.allUnreadableTitle',
    body: 'labeling.dichotomous.degenerate.allUnreadableBody',
    overwriteWarning: null,
    confirm: 'labeling.dichotomous.degenerate.applyAll',
    secondary: 'labeling.dichotomous.degenerate.fallbackManual',
  },
};

interface CountChipProps {
  count: number;
  /** Full literal class strings so Tailwind can statically pick the tokens up. */
  tone: string;
}

function CountChip({ count, tone }: CountChipProps) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 font-mono text-sm font-semibold ${tone}`}
    >
      {count}
    </span>
  );
}

interface CompletionSummaryProps {
  /** The exact set of writes about to happen — its array lengths are what the screen states. */
  plan: DichotomousBulkPlan;
  /** `summary` closes a converged search; the others are the degenerate runs. */
  variant: CompletionVariant;
  /** Runs the bulk write. Offered by every variant — it is the one action always available. */
  onConfirm(): void;
  /** Escape hatch: abandon the plan and go back to labeling this part by hand. */
  onFallbackManual(): void;
  /**
   * Discards the computed plan *and the search that produced it*, starting a fresh run on the same
   * part.
   */
  onRestart(): void;
  /** Blocks the confirm action while a bulk write is in flight. */
  applying?: boolean;
}

/**
 * The last screen before any bulk write: it states, in numbers, exactly how many images are about
 * to be labeled OK and how many Worn, and it requires an explicit confirmation. No write path in
 * dichotomous mode may bypass it.
 *
 * Images set aside as unreadable are part of the plan too, and get their own count line whenever
 * there are any: they are written like the rest, so they are announced like the rest.
 *
 * The counts always come straight from `plan.ok.length` / `plan.worn.length` — including in the
 * degenerate variants, whose plans the caller has already assembled ("everything worn" / "everything
 * OK"). This component therefore never needs to know a plan is degenerate-shaped: it only swaps the
 * surrounding copy, so the safety guarantee (numbers before a write) holds identically everywhere.
 *
 * `summary`'s "cancel" is wired to `onFallbackManual` on purpose: the props carry two escape
 * hatches — give up on the search and label the part by hand (`onFallbackManual`), or throw the
 * run away and search the same part again (`onRestart`) — and at this point of the run declining
 * the computed plan means the former; there is no earlier state to return to.
 */
export function CompletionSummary({
  plan,
  variant,
  onConfirm,
  onFallbackManual,
  onRestart,
  applying = false,
}: CompletionSummaryProps) {
  const { t } = useTranslation();
  const copy = VARIANT_COPY[variant];
  const okCount = plan.ok.length;
  const wornCount = plan.worn.length;
  const unreadableCount = plan.unreadable.length;

  return (
    <div className="flex h-full w-full flex-col items-center justify-center p-4">
      <Card padding="lg" className="flex w-full max-w-lg flex-col gap-5">
        <div className="flex flex-col gap-2">
          <h2 className="section-title">{t(copy.title)}</h2>
          {copy.body && <p className="text-sm text-muted-foreground">{t(copy.body)}</p>}
        </div>

        <Card tier="muted" padding="none" className="flex items-center gap-3 px-4 py-3">
          <CountChip count={okCount} tone="bg-ok-soft text-ok ring-1 ring-ok-ring" />
          <CountChip count={wornCount} tone="bg-worn-soft text-worn ring-1 ring-worn-ring" />
          <p className="text-sm font-medium text-foreground">
            {t('labeling.dichotomous.summary.counts', { ok: okCount, worn: wornCount })}
          </p>
        </Card>

        {unreadableCount > 0 && (
          <Card
            tier="muted"
            padding="none"
            className="flex items-center gap-3 px-4 py-3"
            data-testid="completion-unreadable"
          >
            <CountChip
              count={unreadableCount}
              tone="bg-unreadable-soft text-unreadable ring-1 ring-unreadable-ring"
            />
            <p className="text-sm font-medium text-foreground">
              {t('labeling.dichotomous.summary.unreadableCount', { count: unreadableCount })}
            </p>
          </Card>
        )}

        {copy.overwriteWarning && (
          <p className="text-sm text-muted-foreground">{t(copy.overwriteWarning)}</p>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            disabled={applying}
            onClick={onRestart}
            className="sm:mr-auto"
            data-testid="completion-restart"
          >
            {t('labeling.dichotomous.summary.restart')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onFallbackManual}
            data-testid="completion-secondary"
          >
            {t(copy.secondary)}
          </Button>
          <Button
            type="button"
            loading={applying}
            onClick={onConfirm}
            data-testid="completion-confirm"
          >
            {t(copy.confirm)}
          </Button>
        </div>
      </Card>
    </div>
  );
}
